import _ from 'lodash'
import { MouseEvent } from 'react'
import { connect, useSelector } from 'react-redux'
import { graphql } from '@apollo/client/react/hoc'
import { Divider, Modal, Popover } from 'antd'
import { InfoCircleOutlined, TeamOutlined } from '@ant-design/icons'
import { PROVISIONED_UI_FLAGS } from '@Builder/QuoteDetailPage/logic'
import PremiumTag from '@Builder/QuoteDetailPage/PremiumTag/index'

import Box from '~/core/Box'
import Link from '~/core/Link'
import Text from '~/core/Text'
import Button from '~/core/Button'
import getColor from '~/lib/getColor'
import middleTruncate from '~/lib/middleTruncate'
import { useFlagEnabled } from '~/lib/isFlagEnabled'
import { getUserIdentity } from '~/redux-modules/identity'
import { openModal } from '~/redux-modules/modals'
import { IdentityType } from '~/redux-modules/identity/type'
import TeamManagement from '~/apps/consumer/components/TeamManagement'
import quoteDetailsForInviteAndMember from '../query'
import { quoteDetailsForInviteAndMember as quoteDetailsForInviteAndMemberType } from '../__generated__/quoteDetailsForInviteAndMember'
import ShareQuoteFooter from '../ShareQuoteFooter'
import ShareQuoteForm from '../ShareQuoteForm'
import {
    DestroyInviteDocument,
    DestroyInviteMutation,
    PricingInput,
    RemoveMembershipDocument,
    RemoveMembershipMutation,
} from '~/gql/graphql'

import styles from './styles.css'
import { INDIVIDUAL_ACCESS_INFO, TEAM_ACCESS_INFO } from './constants'

type ShareQuoteModalPropsType = {
    destroyInvite: DestroyInviteMutation
    identity: IdentityType
    isLocatedInAdminPage?: boolean
    isQuotePurchasable?: boolean
    isForwardToPurchaser?: boolean
    onCancel: (evt?: MouseEvent) => void
    placement: string
    priceInput?: PricingInput
    quoteData: quoteDetailsForInviteAndMemberType
    quoteId: string
    quoteName?: string
    removeMembership: RemoveMembershipMutation
    userId?: number
    visible: boolean
    openModal: Function
}

interface PopoverContentType {
    content: string
}

function mapStateToProps() {
    return {
        openModal,
    }
}

const PopoverContent = (props: PopoverContentType) => {
    const { content } = props

    return (
        <Box display="flex" direction="col">
            <Text>{content}</Text>
            <Link
                href="https://help.fictiv.com/en/articles/5392269-how-to-share-a-fictiv-quote"
                target="_blank"
                style={{ marginTop: '10px' }}
            >
                Learn more about sharing
            </Link>
        </Box>
    )
}

const ShareQuoteModal = (props: ShareQuoteModalPropsType) => {
    const {
        destroyInvite,
        identity,
        isLocatedInAdminPage,
        isForwardToPurchaser = false,
        onCancel,
        placement,
        priceInput,
        quoteData,
        quoteId,
        quoteName,
        removeMembership,
        userId,
        visible,
    } = props

    const { businessAccountMembership } = useSelector(getUserIdentity)
    const isTeamsFeatureEnabled =
        !!businessAccountMembership?.businessAccount?.isTeamsFeatureEnabled
    const isEarlyAccessTeamsEnabled = useFlagEnabled(
        PROVISIONED_UI_FLAGS.RELEASE_GET_EARLY_ACCESS_TEAMS,
    )

    const quote = quoteData?.quote
    const quoteOwnerId = quote?.user?.id
    const members = quote?.members?.items
    const invites = quote?.invites?.items
    const team = quote?.team

    return (
        <Modal
            title={
                isForwardToPurchaser
                    ? 'Forward quote details to purchasers'
                    : `Share ${middleTruncate(quoteName)}`
            }
            footer={
                <ShareQuoteFooter
                    isLocatedInAdminPage={isLocatedInAdminPage}
                    isForwardToPurchaser={isForwardToPurchaser}
                    placement={placement}
                    quote={quote}
                    quoteId={quoteId}
                    userId={userId}
                />
            }
            maskClosable
            onCancel={onCancel}
            visible={visible}
            width="630px"
            bodyStyle={{
                maxHeight: '676px',
                overflow: 'auto',
                padding: '16px',
            }}
        >
            <ShareQuoteForm
                id={quoteId}
                isLocatedInAdminPage={isLocatedInAdminPage}
                isQuotePurchasable={quote?.isQuotePurchasable}
                isForwardToPurchaser={isForwardToPurchaser}
                priceInput={priceInput}
                userId={userId}
            />
            <Divider style={{ margin: '16px 0' }} />
            <Box display="block" paddingLeft="8px" paddingRight="8px">
                <Text color="blackAlpha65" fontSize="16px" fontWeight={400}>
                    Individual access
                </Text>
                <Popover
                    title="What does individual access mean?"
                    content={
                        <PopoverContent content={INDIVIDUAL_ACCESS_INFO} />
                    }
                    overlayClassName={styles.popoverOverlay}
                >
                    <InfoCircleOutlined
                        style={{
                            color: getColor('shroudBlue'),
                            marginLeft: '4px',
                        }}
                    />
                </Popover>
                <TeamManagement
                    destroyInvite={destroyInvite}
                    invites={invites || []}
                    id={quoteId}
                    identity={identity}
                    isLocatedInAdminPage={isLocatedInAdminPage}
                    members={members}
                    quoteOwner={quote?.user}
                    quoteOwnerId={quoteOwnerId}
                    removeMembership={removeMembership}
                    showTableHeader={false}
                    userId={userId}
                />
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="between"
                    style={{
                        marginTop:
                            businessAccountMembership && !team ? '16px' : '8px',
                    }}
                >
                    <Box display="block" style={{ width: '100%' }}>
                        <Box
                            style={{
                                backgroundColor:
                                    businessAccountMembership && !team
                                        ? getColor('grey2')
                                        : null,
                                borderRadius: '4px',
                                justifyContent: 'space-between',
                                padding:
                                    businessAccountMembership && !team
                                        ? '8px'
                                        : '0px',
                            }}
                        >
                            <Box
                                display="block"
                                marginTop={
                                    businessAccountMembership && !team
                                        ? '2px'
                                        : '8px'
                                }
                            >
                                {' '}
                                <Text
                                    color="blackAlpha65"
                                    fontSize="16px"
                                    fontWeight={400}
                                >
                                    Team access
                                </Text>
                                <Popover
                                    title="What does team access mean?"
                                    content={
                                        <PopoverContent
                                            content={TEAM_ACCESS_INFO}
                                        />
                                    }
                                    overlayClassName={styles.popoverOverlay}
                                >
                                    <InfoCircleOutlined
                                        style={{
                                            color: getColor('shroudBlue'),
                                            marginLeft: '4px',
                                        }}
                                    />
                                </Popover>
                            </Box>
                            {businessAccountMembership && !team && (
                                <Box marginTop="2px">
                                    <Link
                                        href="https://help.fictiv.com/en/articles/6497159-managing-quotes-and-orders-in-a-team"
                                        target="_blank"
                                        style={{
                                            fontSize: '16px',
                                            fontWeight: 400,
                                        }}
                                    >
                                        Learn how to give your team quote access
                                    </Link>
                                </Box>
                            )}
                        </Box>
                        {team && (
                            <Box alignItems="center" justifyContent="between">
                                <Box alignItems="center">
                                    <TeamOutlined
                                        style={{
                                            color: getColor('blackAlpha85'),
                                            marginRight: '5px',
                                        }}
                                    />
                                    <Text
                                        color="blackAlpha85"
                                        className="membersAccessInfo"
                                    >
                                        Members of{' '}
                                        {
                                            <Text fontWeight={400}>
                                                {team?.name || '<Team Name>'}
                                            </Text>
                                        }{' '}
                                        can access and edit this quote
                                    </Text>
                                </Box>
                            </Box>
                        )}
                        {!team && !businessAccountMembership && (
                            <Box
                                alignItems="center"
                                justifyContent="between"
                                style={{
                                    marginTop: '-10px',
                                }}
                            >
                                <Box alignItems="center" marginTop="12px">
                                    <Text
                                        color="blackAlpha65"
                                        fontSize="14px"
                                        fontWeight={400}
                                        style={{ marginRight: '5px' }}
                                    >
                                        Try
                                    </Text>
                                    <PremiumTag
                                        closePreviousModal={onCancel}
                                        type="premium"
                                    />
                                    <Text
                                        color="blackAlpha65"
                                        fontSize="14px"
                                        fontWeight={400}
                                        style={{ marginLeft: '5px' }}
                                    >
                                        to share quotes with your entire team!
                                    </Text>
                                </Box>
                            </Box>
                        )}
                    </Box>
                    {!isTeamsFeatureEnabled &&
                        isEarlyAccessTeamsEnabled &&
                        team && (
                            <Button
                                style={{
                                    paddingLeft: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                                onClick={() => {
                                    onCancel()
                                    props.openModal('UPSELL')
                                }}
                            >
                                Get team access
                            </Button>
                        )}
                    {!team && !businessAccountMembership && (
                        <Button
                            style={{
                                paddingLeft: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                marginLeft: '7px',
                            }}
                            onClick={() => {
                                props.openModal('UPSELL')
                            }}
                        >
                            Learn more
                        </Button>
                    )}
                </Box>
            </Box>
        </Modal>
    )
}

export default _.flowRight(
    connect(state => ({
        identity: getUserIdentity(state),
    })),
    connect(mapStateToProps, { openModal }),
    graphql(RemoveMembershipDocument, { name: 'removeMembership' }),
    graphql(DestroyInviteDocument, { name: 'destroyInvite' }),
    graphql(quoteDetailsForInviteAndMember, {
        name: 'quoteData',
        skip: (props: ShareQuoteModalPropsType) => !props?.quoteId,
        options: (props: ShareQuoteModalPropsType) => ({
            variables: { id: props?.quoteId, senderId: props?.userId },
        }),
    }),
)(ShareQuoteModal)
